import HiltiInvoicePrices from "../components/HiltiInvoicePrices";
import { ImportAttentionList, ImportStatusSummary } from "../components/ImportStatusSummary";
import { useInHub } from "../lib/hubContext";
import ArticleAssignmentNotice from "../components/ArticleAssignmentNotice";
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import ImportCompletenessNotice from "../components/ImportCompletenessNotice";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { droppedImportFiles, importDocTypeLabel, importFileSize, safeDisplayName } from "../lib/importFiles";
import { takeHandedOffUploadFiles } from "../lib/uploadHandoff";

import FilterDropdown from "../components/FilterDropdown";
import ImportReviewCorrection from "../components/ImportReviewCorrection";
import ImportPdfReview from "../components/ImportPdfReview";
import { useEscapeClosableDetails } from "../lib/useEscapeClosableDetails";
import { useEscapeKey } from "../lib/useEscapeKey";

import {
  confirmImportJob,
  getImportJob,
  getImportQualityReport,
  listImportJobs,
  getImportOutcomes,
  getImportStatus,
  uploadImportBatch,
  type BatchImportEvent,
  type GcSupplierBatchPreview,
  type ImportAcceptancePolicy,
  type ImportJob,
  type ImportJobDetail,
  type ImportQualityReviewKind,
  type UploadImportPreview,
} from "../api/imports";

const DOC_TYPES = [
  "rechnung",
  "einzelauftrag",
  "hauptabrufer",
  "angebot",
  "gutschrift",
  "retoure",
] as const;

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: "Wartet", color: "bg-slate-100 text-slate-700" },
  parsing: { label: "Wird geprüft", color: "bg-blue-100 text-blue-700" },
  review: { label: "Prüfung erforderlich", color: "bg-amber-100 text-amber-700" },
  confirmed: { label: "Bestätigt", color: "bg-emerald-100 text-emerald-700" },
  importing: { label: "Wird übernommen", color: "bg-blue-100 text-blue-700" },
  completed: { label: "Abgeschlossen", color: "bg-emerald-100 text-emerald-700" },
  failed: { label: "Fehlgeschlagen", color: "bg-red-100 text-red-700" },
  rejected: { label: "Abgelehnt", color: "bg-slate-100 text-slate-500" },
};

// Derived from an expired processing claim, not a stored status: a job whose
// request is gone must not keep claiming that it is still being imported.
const INTERRUPTED_STATUS = { label: "Import abgebrochen", color: "bg-amber-100 text-amber-800" };
// Data and original are already written; the batch still owes one shared
// read-model refresh. That is waiting, not a failure.
const COMPLETION_PENDING_STATUS = { label: "Wird fertiggestellt", color: "bg-blue-100 text-blue-700" };

const SOURCE_TYPE_LABELS: Record<string, string> = {
  upload: "Datei-Upload",
  mail: "E-Mail",
  bulk: "Sammel-Import",
  supplier_batch: "Großhändler-Daten",
};

const SUPPLIERS = ["SHT", "GC", "HILTI"] as const;
const AUTO_SELECT_VALUE = "auto";
type UploadDocTypeValue = typeof AUTO_SELECT_VALUE | (typeof DOC_TYPES)[number];
type UploadSupplierValue = typeof AUTO_SELECT_VALUE | (typeof SUPPLIERS)[number];

const DOC_TYPE_OPTIONS: Array<{ value: UploadDocTypeValue; label: string }> = [
  { value: AUTO_SELECT_VALUE, label: "Auto-Erkennung" },
  ...DOC_TYPES.map((type) => ({ value: type, label: docTypeLabel(type) })),
];

const SUPPLIER_OPTIONS: Array<{ value: UploadSupplierValue; label: string }> = [
  { value: AUTO_SELECT_VALUE, label: "Auto-Erkennung" },
  ...SUPPLIERS.map((supplier) => ({ value: supplier, label: supplier })),
];

const QUALITY_SUPPLIER_OPTIONS = ["SHT", "GC"].map((supplier) => ({
  value: supplier,
  label: supplier,
}));

const NEUTRAL_BUTTON_CLASS =
  "inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-default disabled:opacity-50";
const PRIMARY_BUTTON_CLASS =
  "inline-flex h-9 items-center gap-1.5 rounded-md bg-slate-900 px-3 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-default disabled:opacity-50";
const DENSE_NEUTRAL_BUTTON_CLASS =
  "inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50";
const IMPORT_DRAWER_FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

type ImportQualityReport = Awaited<ReturnType<typeof getImportQualityReport>>;

function qualitySignalSummary(code: string, report: ImportQualityReport) {
  if (code === "source_profile_unconfigured") {
    return "Der Prüfumfang für diesen Großhändler wurde noch nicht bestätigt.";
  }
  if (code.startsWith("source_profile_") && code.endsWith("_waiting")) {
    return "Eine regelmäßig erwartete Quelle fehlt; die zugehörige Prüfung wartet.";
  }
  if (code.endsWith("_unavailable")) {
    return "Dieser Prüfschritt ist vorübergehend nicht verfügbar.";
  }
  if (code.startsWith("parse_validation_")) {
    return report.parse_validation.flagged_line_count === 1
      ? "Eine importierte Preiszeile sollte fachlich geprüft werden."
      : "Einige importierte Preiszeilen sollten fachlich geprüft werden.";
  }
  if (code === "reconciliation_no_document_pairs") {
    return "Für den automatischen Belegabgleich wurden keine passenden Rechnungspaare gefunden.";
  }
  if (code === "reconciliation_unmatched_lines") {
    return "Ein Teil der Belegpositionen konnte nicht eindeutig zugeordnet werden.";
  }
  if (code.startsWith("reconciliation_") && code.endsWith("_mismatch_rate")) {
    return "Bei abgeglichenen Positionen unterscheiden sich Werte zwischen UGL und PDF.";
  }
  if (code === "set_price_qa_mismatch") {
    return report.set_price_qa.issue_set_count === 1
      ? "Ein Artikelset benötigt eine Preisprüfung."
      : "Einige Artikelsets benötigen eine Preisprüfung.";
  }
  if (code.startsWith("set_price_qa_missing_")) {
    return report.set_price_qa.issue_set_count === 1
      ? "Für ein Artikelset fehlen benötigte Preis- oder Artikeldaten."
      : "Für einige Artikelsets fehlen benötigte Preis- oder Artikeldaten.";
  }
  return "Dieser Punkt benötigt eine fachliche Kontrolle.";
}

function qualityReviewKindForSignal(
  code: string,
): ImportQualityReviewKind | null {
  if (code.endsWith("_unavailable")) {
    return null;
  }
  return qualityCheckKindForSignal(code);
}

function qualityCheckKindForSignal(
  code: string,
): ImportQualityReviewKind | null {
  if (code === "parse_validation_flagged_lines") {
    return "parse_validation_line";
  }
  if (code.startsWith("reconciliation_")) {
    return "reconciliation_supplier_case";
  }
  if (code.startsWith("set_price_qa_")) {
    return "set_price_qa_set";
  }
  return null;
}

function qualityReviewLinkLabel(kind: ImportQualityReviewKind): string {
  if (kind === "parse_validation_line") return "Preiszeilen prüfen";
  if (kind === "reconciliation_supplier_case") return "Belegabgleich prüfen";
  return "Artikelsets prüfen";
}

function qualityReviewKinds(report: ImportQualityReport): ImportQualityReviewKind[] {
  const activeKinds = new Set(
    report.source_profile.checks
      .filter((check) => check.status === "active")
      .map((check) => check.kind),
  );
  return Array.from(
    new Set(
      report.acceptance.signals
        .map((signal) => qualityReviewKindForSignal(signal.code))
        .filter(
          (kind): kind is ImportQualityReviewKind =>
            kind !== null && activeKinds.has(kind),
        ),
    ),
  );
}

export default function ImportsPage({ section = "upload" }: { section?: "upload" | "history" }) {
  const inHub = useInHub();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedJobId = searchParams.get("job");
  const [qualitySupplier, setQualitySupplier] = useState<string>(() => QUALITY_SUPPLIER_OPTIONS.find(option => option.value === searchParams.get("quality_supplier"))?.value ?? "SHT");
  const [uploadDocType, setUploadDocType] =
    useState<UploadDocTypeValue>(AUTO_SELECT_VALUE);
  const [uploadSupplier, setUploadSupplier] =
    useState<UploadSupplierValue>(AUTO_SELECT_VALUE);
  const [dragActive, setDragActive] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const waitingFiles = useRef<File[]>([]);
  const inFlightFiles = useRef<File[]>([]);
  const [readingFolder, setReadingFolder] = useState(false);
  const folderReads = useRef<Promise<void>>(Promise.resolve());
  const [batchEvents, setBatchEvents] = useState<BatchImportEvent[]>([]);
  // The batch defers one shared read-model refresh to its end; say so instead
  // of leaving the last files looking unfinished.
  const [batchFinalization, setBatchFinalization] = useState("");
  const [batchDone, setBatchDone] = useState(false);
  const [batchFinalizing, setBatchFinalizing] = useState(false);
  const [batchTotal, setBatchTotal] = useState(0);
  const [activeFile, setActiveFile] = useState("");
  const [batchStopped, setBatchStopped] = useState(false);
  // With the server queue on, a file is stored within seconds and processed
  // afterwards. Its row then follows the job until it is done.
  const [batchQueued, setBatchQueued] = useState(false);
  const [selectionError, setSelectionError] = useState("");
  const uploadController = useRef<AbortController | null>(null);
  useEffect(() => () => uploadController.current?.abort(), []);

  const jobsQuery = useQuery({
    queryKey: ["imports"],
    queryFn: () => listImportJobs(),
    refetchInterval: 5000,
  });

  useEffect(() => {
    if (window.location.hash === "#daten-pruefen" && !jobsQuery.isLoading) document.getElementById("daten-pruefen")?.scrollIntoView({ block: "start" });
  }, [jobsQuery.isLoading]);

  const jobDetailQuery = useQuery({
    queryKey: ["imports", "job", selectedJobId],
    queryFn: () => getImportJob(selectedJobId ?? ""),
    enabled: Boolean(selectedJobId),
    refetchInterval: (query) =>
      query.state.data && !query.state.data.interrupted
      && ["pending", "parsing", "confirmed", "importing"].includes(query.state.data.status) ? 2000 : false,
  });

  const workingJobIds = batchEvents
    .filter((event) => event.job_id && (event.outcome === "queued" || event.outcome === "pending"))
    .map((event) => event.job_id as string);
  const outcomesQuery = useQuery({
    queryKey: ["imports", "outcomes", workingJobIds.join(",")],
    queryFn: () => getImportOutcomes(workingJobIds),
    enabled: batchQueued && workingJobIds.length > 0,
    refetchInterval: 3000,
  });
  useEffect(() => {
    const outcomes = outcomesQuery.data;
    if (!outcomes?.length) return;
    const byJob = new Map(outcomes.map((item) => [item.job_id, item]));
    setBatchEvents((previous) => previous.map((event) => {
      const next = event.job_id ? byJob.get(event.job_id) : undefined;
      return next && (next.outcome !== event.outcome || next.message !== event.message)
        ? { ...event, outcome: next.outcome, message: next.message }
        : event;
    }));
    if (outcomes.some((item) => item.done)) void queryClient.invalidateQueries({ queryKey: ["imports"], exact: true });
  }, [outcomesQuery.data, queryClient]);
  const batchFinished = batchEvents.filter((event) => event.outcome !== "queued" && event.outcome !== "pending").length;

  const qualityQuery = useQuery({
    queryKey: ["imports", "quality", qualitySupplier],
    queryFn: () => getImportQualityReport(qualitySupplier),
    retry: false,
  });

  const uploadMutation = useMutation({
    mutationFn: async (args: { files: File[]; docType: string | null; supplier: string | null }) => {
      const controller = new AbortController();
      uploadController.current = controller;
      try {
        let files = args.files;
        let first = true;
        while (files.length && !controller.signal.aborted) {
          inFlightFiles.current = files;
          await uploadImportBatch(files, first ? args.docType : null, first ? args.supplier : null, controller.signal, (event) => {
            if (event.type === "start") {
              setBatchFinalization("");
              setBatchDone(false);
              setBatchFinalizing(false);
              setBatchTotal((previous) => previous + (event.total ?? 0));
            }
            if (event.type === "processing") setActiveFile(event.name ?? "");
            if (event.type === "finalizing") setBatchFinalizing(true);
            if (event.type === "done") {
              setBatchDone(true);
              setBatchFinalization(
                event.read_model_refresh === "failed"
                  ? "Die Belege sind gespeichert, das Fertigstellen hat aber nicht geklappt. Bitte den Importverlauf prüfen."
                  : event.deferred_jobs
                    ? `${event.deferred_jobs} ${event.deferred_jobs === 1 ? "Beleg" : "Belege"} fertig.`
                    : "",
              );
            }
            if (event.type === "result") {
              if (event.outcome === "queued") setBatchQueued(true);
              setBatchEvents((previous) => [...previous, event]);
              setActiveFile("");
              if (event.input_done && event.input_index !== undefined) {
                const completed = files[event.input_index];
                waitingFiles.current = waitingFiles.current.filter((file) => file !== completed);
                setSelectedFiles([...waitingFiles.current]);
              }
              void queryClient.invalidateQueries({ queryKey: ["imports"] });
            }
          });
          waitingFiles.current = waitingFiles.current.filter((file) => !files.includes(file));
          setSelectedFiles([...waitingFiles.current]);
          await folderReads.current;
          files = [...waitingFiles.current];
          first = false;
        }
      } catch (error) {
        if (!controller.signal.aborted) throw error;
      } finally {
        inFlightFiles.current = [];
        uploadController.current = null;
        void queryClient.invalidateQueries({ queryKey: ["imports"] });
      }
    },
  });

  function chooseFiles(files: File[]) {
    if (!files.length) return;
    const combined = [...waitingFiles.current, ...files];
    const total = combined.reduce((sum, file) => sum + file.size, 0);
    if (combined.length > 200 || total > 200 * 1024 * 1024) {
      setSelectionError("Maximal 200 Dateien und 200 MiB pro Stapel.");
      return;
    }
    setSelectionError("");
    waitingFiles.current = combined;
    setSelectedFiles(combined);
    setUploadDocType(AUTO_SELECT_VALUE);
    setUploadSupplier(AUTO_SELECT_VALUE);
  }

  // Auf der Übersicht abgelegte Dateien landen direkt in der Warteschlange (#630).
  useEffect(() => {
    const handedOff = takeHandedOffUploadFiles();
    if (handedOff.length) chooseFiles(handedOff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The decision just made in the review, so the drawer can say the case is
  // done and offer the next one instead of leaving the user to work that out.
  const [decided, setDecided] = useState<{ jobId: string; decision: "confirm" | "reject" } | null>(null);
  const reviewedHiltiComplete = jobDetailQuery.data?.supplier === "HILTI"
    && jobDetailQuery.data.source_type === "upload"
    && jobDetailQuery.data.user_decision === "confirm"
    && jobDetailQuery.data.status === "completed"
    && !jobDetailQuery.data.interrupted;
  useEffect(() => {
    if (reviewedHiltiComplete) void queryClient.invalidateQueries({ queryKey: ["imports", "status"] });
  }, [reviewedHiltiComplete, selectedJobId, queryClient]);
  const completedDecision = decided?.jobId === selectedJobId ? decided.decision
    : reviewedHiltiComplete ? "confirm" : null;
  const statusQuery = useQuery({ queryKey: ["imports", "status"], queryFn: getImportStatus, refetchInterval: 15000, retry: false });
  const confirmMutation = useMutation({
    mutationFn: async (args: { jobId: string; decision: "confirm" | "reject" }) =>
      confirmImportJob(args.jobId, args.decision),
    onSuccess: async (result, args) => {
      await queryClient.cancelQueries({ queryKey: ["imports", "job", result.job_id] });
      if (result.status === "importing") {
        queryClient.setQueryData<ImportJobDetail>(["imports", "job", result.job_id], (job) =>
          job ? { ...job, status: "importing", user_decision: args.decision } : job);
      }
      selectJob(result.job_id);
      // After selectJob: it clears a notice for another case and would
      // otherwise see the stale one and clear this one too.
      if (["completed", "rejected"].includes(result.status)) setDecided({ jobId: result.job_id, decision: args.decision });
      void queryClient.invalidateQueries({ queryKey: ["imports"] });
      void queryClient.invalidateQueries({ queryKey: ["imports", "job", result.job_id] });
    },
  });

  function selectJob(jobId: string | null) {
    if (jobId !== decided?.jobId) setDecided(null);
    const next = new URLSearchParams(searchParams);
    if (jobId) next.set("job", jobId);
    else next.delete("job");
    setSearchParams(next, { replace: true });
  }

  function handleUpload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedFiles.length || uploadMutation.isPending) return;
    setBatchEvents([]);
    setBatchQueued(false);
    setBatchTotal(0);
    setActiveFile("");
    setBatchStopped(false);
    uploadMutation.mutate({ files: selectedFiles,
      docType: selectedFiles.length === 1 && !selectedFiles[0].name.toLowerCase().endsWith(".zip") ? selectedUploadValue(uploadDocType) : null,
      supplier: selectedFiles.length === 1 && !selectedFiles[0].name.toLowerCase().endsWith(".zip") ? selectedUploadValue(uploadSupplier) : null });
  }

  return (
    <main className="mx-auto w-full min-w-0 max-w-7xl px-6 py-6"
      onDragOver={(event) => { if (event.dataTransfer.types.includes("Files")) { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; setDragActive(true); } }}
      onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragActive(false); }}
      onDrop={(event) => {
        event.preventDefault(); setDragActive(false); setReadingFolder(true);
        const read = droppedImportFiles(event.dataTransfer).then(chooseFiles).catch((error: unknown) => {
          setSelectionError(error instanceof Error ? error.message : "Der Ordner konnte nicht gelesen werden. Bitte als ZIP hinzufügen.");
        });
        folderReads.current = Promise.all([folderReads.current, read]).then(() => { setReadingFolder(false); });
      }}>
      {inHub ? null : <div>
        <h1 className="text-2xl font-semibold text-slate-900">{section === "upload" ? "Belege hochladen" : "Importe"}</h1>
      </div>}

      <div className={`${inHub ? "" : "mt-5 "}min-w-0 space-y-6`}>
        {section === "history" ? <ImportAttentionList onSelect={selectJob} /> : null}
        {section === "history" ? <ImportStatusSummary onSelect={selectJob} /> : null}
        {section === "upload" ? <section className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="text-lg font-medium text-slate-900">Dateien hochladen</h2>
          <form onSubmit={handleUpload} className="mt-4">
            <div className={`relative rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${dragActive ? "border-emerald-600 bg-emerald-50" : "border-slate-300 bg-slate-50/70 hover:border-slate-400"}`}>
              <svg aria-hidden="true" className="mx-auto mb-3 h-8 w-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0L7 9m5-5 5 5M4 16v4h16v-4" />
              </svg>
              <p className="text-lg font-medium text-slate-900">{dragActive ? "Dateien hier loslassen" : "Dateien hierher ziehen"}</p>
              <p className="mt-2 text-sm text-slate-600">PDFs, Ordner und ZIP-Dateien – auch verschiedene Belegarten und Großhändler zusammen.</p>
              <button type="button" disabled={readingFolder} onClick={() => fileInput.current?.click()}
                className={`${NEUTRAL_BUTTON_CLASS} mt-5`}>{selectedFiles.length ? "Weitere Dateien hinzufügen" : "Dateien wählen"}</button>
              <input ref={fileInput} id="import-file" type="file" name="file" aria-label="Datei" multiple
                disabled={readingFolder} className="hidden"
                onChange={(event) => { chooseFiles(Array.from(event.currentTarget.files ?? [])); event.currentTarget.value = ""; }} />
            </div>
            <p className="mt-3 text-xs text-slate-600">Auch UGL und DATANORM möglich. Bis zu 200 Dateien, zusammen 200 MiB, je Datei 50 MiB. Bei ZIP zählt auch der entpackte Inhalt.</p>
            {readingFolder && <p role="status" className="mt-3 text-sm text-slate-600">Ordner wird gelesen …</p>}
            {selectedFiles.length === 1 && !uploadMutation.isPending && !selectedFiles[0].name.toLowerCase().endsWith(".zip") && <details className="mt-4 text-sm text-slate-600">
              <summary className="w-fit cursor-pointer rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600">Erkennung korrigieren</summary>
              <p className="mt-2">Nur nötig, wenn diese Datei zuvor nicht richtig erkannt wurde.</p>
              <fieldset disabled={uploadMutation.isPending} className="mt-3 grid max-w-lg gap-4 sm:grid-cols-2">
              <div className="block text-sm">
                <span className="text-slate-700">Belegtyp</span>
                <div className="mt-1">
                  <FilterDropdown
                    label={uploadDocType === AUTO_SELECT_VALUE ? "Auto-Erkennung" : "Belegtyp"}
                    options={DOC_TYPE_OPTIONS}
                    value={uploadDocType}
                    defaultValue={AUTO_SELECT_VALUE}
                    onChange={setUploadDocType}
                    ariaLabel="Belegtyp auswählen"
                  />
                </div>
              </div>
              <div className="block text-sm">
                <span className="text-slate-700">Großhändler</span>
                <div className="mt-1">
                  <FilterDropdown
                    label={uploadSupplier === AUTO_SELECT_VALUE ? "Auto-Erkennung" : "Großhändler"}
                    options={SUPPLIER_OPTIONS}
                    value={uploadSupplier}
                    defaultValue={AUTO_SELECT_VALUE}
                    onChange={setUploadSupplier}
                    ariaLabel="Großhändler auswählen"
                  />
                </div>
              </div>
              </fieldset>
            </details>}
            {selectedFiles.length > 0 && <div className="mt-5">
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-slate-900">{selectedFiles.length} {selectedFiles.length === 1 ? "Datei in der Warteschlange" : "Dateien in der Warteschlange"}</p>
                <button type="button" className="text-sm text-slate-600 underline underline-offset-2" disabled={uploadMutation.isPending}
                  onClick={() => { waitingFiles.current = []; setSelectedFiles([]); setSelectionError(""); }}>Auswahl leeren</button>
              </div>
              <ul className="max-h-56 divide-y divide-slate-200 overflow-y-auto rounded-lg border border-slate-200" aria-label="Ausgewählte Dateien">
                {selectedFiles.map((file, index) => <li className="flex items-center gap-3 px-4 py-3 text-sm" key={index}>
                  <span className="min-w-0 flex-1 break-all text-slate-900">{file.name}</span>
                  <span className="shrink-0 text-slate-600">{importFileSize(file.size)}</span>
                  <button type="button" disabled={inFlightFiles.current.includes(file)} aria-label={`${file.name} entfernen`} className="rounded p-1 text-slate-600 hover:bg-slate-100"
                    onClick={() => { waitingFiles.current = waitingFiles.current.filter((_, i) => i !== index); setSelectedFiles([...waitingFiles.current]); setUploadDocType(AUTO_SELECT_VALUE); setUploadSupplier(AUTO_SELECT_VALUE); }}>×</button>
                </li>)}
              </ul>
            </div>}
            {selectionError && <p role="alert" className="mt-3 text-sm text-red-700">{selectionError}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={uploadMutation.isPending || readingFolder || !selectedFiles.length}
                className={`${PRIMARY_BUTTON_CLASS} justify-center`}
              >
                {uploadMutation.isPending ? "Dateien werden geprüft …" : "Dateien importieren"}
              </button>
              {uploadMutation.isPending && <button type="button" className={NEUTRAL_BUTTON_CLASS}
                onClick={() => { setBatchStopped(true); uploadController.current?.abort(); }}>Import abbrechen</button>}
              {uploadMutation.isError && (
                <span className="text-sm text-red-600">{safeUiMessage(uploadMutation.error.message)}</span>
              )}
            </div>
          </form>
          {(uploadMutation.isPending || batchTotal > 0 || batchStopped) && <div className="mt-4 space-y-3">
            <p role="status" className="break-all text-sm text-slate-600">
              {batchStopped ? "Abgebrochen. Die laufende Datei kann noch abgeschlossen werden; Status im Importverlauf prüfen. Bereits übernommene Dateien bleiben erhalten."
                : batchQueued && uploadMutation.isPending ? `${batchEvents.length} von ${batchTotal} Dateien gespeichert`
                : batchQueued && batchFinished < batchEvents.length ? `Alle ${batchEvents.length} Dateien gespeichert. Die Verarbeitung läuft auf dem Server – du kannst diese Seite schließen. (${batchFinished} von ${batchEvents.length} fertig)`
                : batchQueued ? `Alle ${batchEvents.length} Dateien verarbeitet.`
                : batchFinalization ? batchFinalization
                : batchFinalizing && !batchDone ? "Alle Dateien gespeichert. Wird jetzt fertiggestellt – das dauert ein paar Minuten."
                : batchTotal && batchEvents.length === batchTotal && !batchDone ? "Alle Dateien übernommen. Der gemeinsame Abschluss läuft – das dauert einige Minuten."
                : batchTotal ? `${batchEvents.length} von ${batchTotal} Dateien verarbeitet${activeFile ? ` – ${activeFile}` : ""}`
                : "Dateien werden hochgeladen und ZIP-Archive geprüft …"}
            </p>
            {batchTotal > 0 && <div role="progressbar" aria-label="Importfortschritt" aria-valuemin={0} aria-valuemax={batchTotal} aria-valuenow={batchQueued ? batchFinished : batchEvents.length} className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-600 transition-all" style={{ width: `${Math.min(100, (batchQueued ? batchFinished : batchEvents.length) / batchTotal * 100)}%` }} /></div>}
            <ul aria-label="Ergebnisse des Imports" className="max-h-80 divide-y divide-slate-200 overflow-y-auto rounded-md border border-slate-200">
              {batchEvents.map((result, index) => <li key={index} className="grid items-center gap-x-4 gap-y-1 px-3 py-2 text-sm sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_auto]">
                <p className="break-all font-medium text-slate-900 sm:row-span-2">{result.name}</p>
                <p className={result.outcome === "rejected" ? "text-red-700" : "text-slate-600"}>{safeUiMessage(result.message ?? "")}</p>
                {result.existing_source && <div className="contents text-slate-600">
                  <p className="text-xs sm:col-start-2 sm:row-start-auto">{result.existing_source.imported_at
                    ? `Ursprünglich importiert am ${new Date(result.existing_source.imported_at).toLocaleString("de-AT")}`
                    : "Das ursprüngliche Importdatum ist nicht gespeichert."}</p>
                  <Link className="font-medium text-emerald-700 underline underline-offset-2 sm:col-start-3 sm:row-span-2 sm:row-start-1"
                    to={`/coverage?file=${encodeURIComponent(result.existing_source.file_ref)}`}>Vorhandenen Beleg öffnen</Link>
                </div>}
                {result.job_id && <button type="button" className={`${DENSE_NEUTRAL_BUTTON_CLASS} sm:col-start-3 sm:row-span-2 sm:row-start-1`}
                  onClick={() => selectJob(result.job_id ?? null)}>Details prüfen</button>}
              </li>)}
            </ul>
          </div>}
        </section> : null}

        {section === "history" ? <ImportHistory
          jobs={jobsQuery.data ?? []}
          isLoading={jobsQuery.isLoading}
          selectedJobId={selectedJobId}
          onSelect={selectJob}
        /> : null}

        {section === "history" ? <ImportQualityPanel
          supplier={qualitySupplier}
          setSupplier={setQualitySupplier}
          isLoading={qualityQuery.isLoading}
          error={qualityQuery.isError ? qualityQuery.error.message : null}
          report={qualityQuery.data}
        /> : null}
      </div>

      <ImportReviewDrawer
        open={Boolean(selectedJobId)}
        job={jobDetailQuery.data}
        isLoading={jobDetailQuery.isLoading}
        error={jobDetailQuery.isError ? jobDetailQuery.error.message : null}
        confirmPending={confirmMutation.isPending}
        confirmError={confirmMutation.isError ? confirmMutation.error.message : null}
        onConfirm={(jobId) => confirmMutation.mutate({ jobId, decision: "confirm" })}
        onReject={(jobId) => confirmMutation.mutate({ jobId, decision: "reject" })}
        onClose={() => selectJob(null)}
        completion={completedDecision ? {
          decision: completedDecision,
          openCases: (statusQuery.data?.attention ?? []).filter((item) => item.job_id !== selectedJobId).map((item) => item.job_id),
        } : null}
        onOpenCase={(jobId) => selectJob(jobId)}
      />
    </main>
  );
}

function ImportQualityPanel(props: {
  supplier: string;
  setSupplier: (supplier: string) => void;
  isLoading: boolean;
  error: string | null;
  report: Awaited<ReturnType<typeof getImportQualityReport>> | undefined;
}) {
  const reviewKinds = props.report ? qualityReviewKinds(props.report) : [];
  const qualityDetails = useEscapeClosableDetails();
  const activeCheckKinds = new Set(
    props.report?.source_profile.checks
      .filter((check) => check.status === "active")
      .map((check) => check.kind) ?? [],
  );
  const visibleSignals = props.report?.acceptance.signals.filter((signal) => {
    const kind = qualityCheckKindForSignal(signal.code);
    if (signal.code.startsWith("source_profile_") && signal.code.endsWith("_waiting")) return false;
    return (kind === null || activeCheckKinds.has(kind)) && !reviewKinds.includes(kind!);
  }) ?? [];
  const inactiveChecks =
    props.report?.source_profile.checks.filter(
      (check) => check.status !== "active",
    ) ?? [];
  return (
    <section id="daten-pruefen" className="scroll-mt-4 rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-slate-900">Daten prüfen</h2>
        </div>
        <FilterDropdown
          label={props.supplier}
          options={QUALITY_SUPPLIER_OPTIONS}
          value={props.supplier}
          defaultValue={props.supplier}
          onChange={props.setSupplier}
          ariaLabel="Großhändler auswählen"
        />
      </div>

      <ArticleAssignmentNotice supplier={props.supplier} />
      {props.isLoading && (
        <p className="mt-3 text-sm text-slate-500">Qualitätsbericht wird geladen.</p>
      )}
      {props.error && (
        <p className="mt-3 text-sm text-red-600">{safeUiMessage(props.error)}</p>
      )}
      {props.report && (
        <div className="mt-4">
          {reviewKinds.length > 0 ? (
            <div className="divide-y divide-slate-200">
              {reviewKinds.map((kind) => (
                <div
                  key={kind}
                  className="flex min-w-0 flex-wrap items-center justify-between gap-3 py-4"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">
                      {kind === "parse_validation_line" ? "Preisangaben" : qualityReviewKindLabel(kind)}
                    </div>
                    <div className="mt-0.5 text-xs tabular-nums text-slate-500">
                      {qualityReviewCountLabel(props.report!, kind)}
                    </div>
                  </div>
                  <Link
                    to={`/imports/review?supplier=${encodeURIComponent(props.supplier)}&check=${kind}&status=all`}
                    className={DENSE_NEUTRAL_BUTTON_CLASS}
                  >
                    {kind === "parse_validation_line" ? "Hinweise ansehen" : qualityReviewLinkLabel(kind)} →
                  </Link>
                </div>
              ))}
            </div>
          ) : visibleSignals.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">
              Keine weiteren Auffälligkeiten aus den verfügbaren Prüfungen.
            </p>
          ) : null}

              {visibleSignals.length > 0 ? (
                <ul className="mt-3 space-y-3">
                  {visibleSignals.map((signal) => (
                    <li
                      key={`${signal.source}-${signal.code}`}
                      className="text-slate-600"
                    >
                      <p className="text-sm font-medium leading-5 text-slate-800">
                        {qualitySignalSummary(signal.code, props.report!)}
                      </p>

                    </li>
                  ))}
                </ul>
              ) : null}
          {inactiveChecks.length > 0 ? (
            <details
              open={qualityDetails.open}
              className="mt-4 border-t border-slate-100 pt-3"
            >
              <summary
                ref={qualityDetails.summaryRef}
                onClick={qualityDetails.onSummaryClick}
                className="cursor-pointer rounded-sm text-xs font-medium text-slate-600 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1"
              >
                Weitere Prüfungen
              </summary>
              {inactiveChecks.length > 0 ? (
                <div className="mt-4">
                  <ul className="mt-2 space-y-2">
                    {inactiveChecks.map((check) => (
                      <li
                        key={check.kind}
                        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-slate-800">
                            {qualityReviewKindLabel(check.kind)}
                          </span>
                          <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">
                            {qualityCheckStatusLabel(check.status)}
                          </span>
                        </div>
                        <p className="mt-1 text-xs leading-5 text-slate-500">
                          {check.reason}
                        </p>
                        {check.status === "waiting_for_source" ? (
                          <Link to="/coverage?tab=profiles" className="mt-2 inline-flex text-xs font-medium text-emerald-700 hover:underline">Datenquellen ansehen →</Link>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </details>
          ) : null}
        </div>
      )}
    </section>
  );
}

function qualityReviewCountLabel(
  report: ImportQualityReport,
  kind: ImportQualityReviewKind,
): string {
  if (kind === "parse_validation_line") {
    const count = report.parse_validation.flagged_line_count;
    return `${count.toLocaleString("de-AT")} ${
      count === 1 ? "Hinweis insgesamt" : "Hinweise insgesamt"
    }`;
  }
  if (kind === "reconciliation_supplier_case") return "1 Abgleich";
  const count = report.set_price_qa.issue_set_count;
  return `${count.toLocaleString("de-AT")} ${
    count === 1 ? "Artikelset" : "Artikelsets"
  }`;
}

function qualityReviewKindLabel(kind: ImportQualityReviewKind): string {
  if (kind === "parse_validation_line") return "Preiszeilen";
  if (kind === "reconciliation_supplier_case") return "Belegabgleich";
  return "Artikelsets";
}

function qualityCheckStatusLabel(status: string): string {
  return status === "waiting_for_source"
    ? "Wartet auf Quelle"
    : "Nicht anwendbar";
}

function ImportHistory(props: {
  jobs: ImportJob[];
  isLoading: boolean;
  selectedJobId: string | null;
  onSelect: (jobId: string) => void;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5">
      <h2 className="text-lg font-medium text-slate-900">Bisherige Importe</h2>
      {props.isLoading && <p className="mt-3 text-sm text-slate-500">Importe werden geladen.</p>}
      {!props.isLoading && props.jobs.length === 0 && (
        <p className="mt-3 text-sm text-slate-500">
          Hier erscheinen deine Dateien nach dem ersten Import.
        </p>
      )}
      {props.jobs.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-[760px] text-sm sm:min-w-full">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Import</th>
                <th className="px-4 py-2">Quelle</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Datum</th>
                <th className="px-4 py-2 text-right">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {props.jobs.map((job) => {
                const missingOriginal = job.status === "review" && job.review_file_available === false;
                const status = missingOriginal ? {label:"Original fehlt",color:"bg-slate-100 text-slate-700"}
                  : job.interrupted ? (job.supplier === "HILTI" ? { ...INTERRUPTED_STATUS, label: "Abschluss offen" } : INTERRUPTED_STATUS)
                  : job.completion_pending ? COMPLETION_PENDING_STATUS
                  : STATUS_LABELS[job.status] ?? STATUS_LABELS.pending;
                const selected = props.selectedJobId === job.job_id;
                return (
                  <tr key={job.job_id} className={selected ? "bg-slate-50" : "hover:bg-slate-50"}>
                    <td className="px-4 py-2 text-slate-900">
                      {safeDisplayName(job.source_filename) || "Import"}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{sourceTypeLabel(job.source_type)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {job.created_at ? new Date(job.created_at).toLocaleString("de-DE") : "–"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => props.onSelect(job.job_id)}
                        className={DENSE_NEUTRAL_BUTTON_CLASS}
                      >
                        {missingOriginal ? "Original ergänzen" : job.status === "review" ? "Prüfen" : "Ansehen"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type ImportReviewProps = {
  onPage?: (page: number) => void;
  job: ImportJobDetail | undefined;
  isLoading: boolean;
  error: string | null;
  confirmPending: boolean;
  confirmError: string | null;
  onConfirm: (jobId: string) => void;
  onReject: (jobId: string) => void;
};

type ReviewCompletion = { decision: "confirm" | "reject"; openCases: string[] };

function ImportReviewDrawer(
  props: ImportReviewProps & {
    open: boolean;
    onClose: () => void;
    completion: ReviewCompletion | null;
    onOpenCase: (jobId: string) => void;
  },
) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEscapeKey(props.onClose, props.open);
  useEffect(() => {
    if (!props.open) return;
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() =>
      closeButtonRef.current?.focus(),
    );
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      const returnTarget = returnFocusRef.current;
      returnFocusRef.current = null;
      if (returnTarget?.isConnected) {
        window.requestAnimationFrame(() => returnTarget.focus());
      }
    };
  }, [props.open]);
  if (!props.open) return null;

  const job = props.job;
  const status = job?.result_summary?.status === "already_present"
    ? { label: "Bereits vorhanden", color: "bg-slate-100 text-slate-700" }
    : job?.interrupted ? (job.supplier === "HILTI" ? { ...INTERRUPTED_STATUS, label: "Abschluss offen" } : INTERRUPTED_STATUS)
    : job?.completion_pending ? COMPLETION_PENDING_STATUS
    : job ? STATUS_LABELS[job.status] ?? STATUS_LABELS.pending : null;
  const title = job ? safeDisplayName(job.source_filename) || "Import" : "Import-Prüfung";

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-900/20"
        aria-label="Import-Prüfung schließen"
        onClick={props.onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-review-title"
        onKeyDown={trapImportDrawerFocus}
        className="absolute inset-y-0 right-0 flex w-[min(90rem,100vw)] flex-col border-l border-slate-200 bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Import-Prüfung
            </div>
            <h2
              id="import-review-title"
              className="mt-1 truncate text-lg font-semibold text-slate-900"
            >
              {title}
            </h2>
            {job && (
              <p className="mt-1 text-sm text-slate-500">
                {sourceTypeLabel(job.source_type)}
                {job.supplier ? ` · ${job.supplier}` : ""}
              </p>
            )}
          </div>
          <div className="flex flex-none items-center gap-2">
            {status && (
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.color}`}
              >
                {status.label}
              </span>
            )}
            <button
              ref={closeButtonRef}
              type="button"
              onClick={props.onClose}
              className="h-8 rounded-md px-2 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            >
              Schließen
            </button>
          </div>
        </header>
        {job?.supplier === "HILTI" && job.status === "importing" && !job.interrupted && (
          <p role="status" className="mx-5 mt-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            Die Übernahme läuft. Du kannst diese Prüfung schließen und den Stand später im Importverlauf öffnen.
            Sobald Beleg und Auswertungen bereit sind, erscheint hier der Abschluss.
          </p>
        )}
        {props.completion && <ReviewCompletionNotice
          completion={props.completion}
          docId={props.completion.decision === "confirm" ? (job?.parse_result as { doc_id?: string } | null)?.doc_id ?? null : null}
          jobId={job?.job_id ?? null}
          onOpenCase={props.onOpenCase}
          onClose={props.onClose}
        />}
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          {job?.source_filename?.toLowerCase().endsWith(".pdf") ? <ImportPdfReview key={job.job_id} job={job}>
            {(detail, onPage) => <ImportReviewBody {...props} job={detail} onPage={onPage} />}
          </ImportPdfReview> : <ImportReviewBody {...props} />}
        </div>
      </aside>
    </div>
  );
}

// Tells the user the case is finished and where to go next. Without it an
// accepted import looked the same as one still waiting for a decision.
function ReviewCompletionNotice({ completion, docId, jobId, onOpenCase, onClose }: {
  completion: ReviewCompletion;
  docId: string | null;
  jobId: string | null;
  onOpenCase: (jobId: string) => void;
  onClose: () => void;
}) {
  const verb = completion.decision === "confirm" ? "Übernommen" : "Abgelehnt";
  const next = completion.openCases[0];
  const nextRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => { nextRef.current?.focus(); }, []);
  return <div role="status" className="border-b border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900">
    <p className="font-semibold">✓ {verb} – {next ? "dieser Fall ist erledigt." : "alles erledigt."}</p>
    {docId && <Link className="mt-1 inline-block underline underline-offset-2" to={`/belege/${encodeURIComponent(docId)}`}
      state={jobId ? { from: `/imports?job=${encodeURIComponent(jobId)}` } : undefined}>Beleg ansehen</Link>}
    <div className="mt-3 flex flex-wrap gap-2">
      {next && <button ref={nextRef} type="button" onClick={() => onOpenCase(next)}
        className="h-9 rounded-md bg-emerald-600 px-3 font-medium text-white hover:bg-emerald-700">
        Nächster offener Fall ({completion.openCases.length})
      </button>}
      <button ref={next ? undefined : nextRef} type="button" onClick={onClose}
        className="h-9 rounded-md border border-emerald-300 bg-white px-3 font-medium text-emerald-900 hover:bg-emerald-100">
        Zurück zur Übersicht
      </button>
    </div>
  </div>;
}

function trapImportDrawerFocus(event: KeyboardEvent<HTMLElement>) {
  if (event.key !== "Tab") return;
  const focusable = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(
      IMPORT_DRAWER_FOCUSABLE_SELECTOR,
    ),
  ).filter((element) => element.getClientRects().length > 0);
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (
    event.shiftKey &&
    (active === first || !event.currentTarget.contains(active))
  ) {
    event.preventDefault();
    last.focus();
    return;
  }
  if (
    !event.shiftKey &&
    (active === last || !event.currentTarget.contains(active))
  ) {
    event.preventDefault();
    first.focus();
  }
}

function ImportReviewBody(props: ImportReviewProps) {
  const [correctionEditing, setCorrectionEditing] = useState(false);
  if (props.isLoading) {
    return <p className="text-sm text-slate-500">Import-Details werden geladen.</p>;
  }
  if (props.error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3">
        <p className="text-sm text-red-700">{safeUiMessage(props.error)}</p>
      </div>
    );
  }
  if (!props.job) {
    return (
      <p className="text-sm text-slate-500">
        Wähle einen Import aus der Historie oder starte eine neue Prüfung.
      </p>
    );
  }

  const job = props.job;
  const gcPreview = gcSupplierBatchPreview(job.parse_result);
  const alreadyPresent = job.result_summary?.status === "already_present";
  const uploadPreview = uploadImportPreview(job.parse_result);
  const uploadBlockedByDetection =
    uploadPreview?.detection?.review_required === true &&
    uploadPreview.manual_selection?.fully_manual !== true;
  const uploadBlockedByCapabilities =
    uploadPreview?.import_capabilities?.canonical_import_allowed === false;
  const canConfirm =
    job.status === "review" &&
    !uploadPreview?.document_completeness?.blocking &&
    !correctionEditing && uploadPreview?.review_file_available !== false && uploadPreview?.acceptance_policy?.decision !== "fail" &&
    (!gcPreview || gcPreview.capabilities.structured_import_allowed) &&
    !uploadBlockedByDetection &&
    !uploadBlockedByCapabilities;

  return (
    <div className="space-y-4">
      <ImportCompletenessNotice report={uploadPreview?.document_completeness} onPage={props.onPage} />
      {(job.status === "rejected" || (job.source_type === "upload" && job.source_filename?.toLowerCase().endsWith(".pdf") && job.status === "review")) && <ImportReviewCorrection
        key={`${job.job_id}-${job.updated_at}-${job.status}`}
        job={job} docTypes={DOC_TYPES.map(value => ({ value, label: docTypeLabel(value) }))}
        suppliers={SUPPLIERS} onEditing={setCorrectionEditing}
      />}
      {uploadPreview?.hilti_evidence ? <HiltiInvoicePrices evidence={uploadPreview.hilti_evidence} context={uploadPreview.hilti_cost_center_context} onPage={props.onPage} /> : gcPreview ? (
        <GcSupplierBatchPreviewSection preview={gcPreview} />
      ) : uploadPreview ? (
        uploadPreview.intake_kind === "single_pdf" && job.status === "review" ? null : <UploadPreviewSection
          preview={uploadPreview}
          onPage={props.onPage}
          status={alreadyPresent ? "duplicate" : job.interrupted ? "interrupted" : job.completion_pending ? "completion_pending" : job.status}
          warnings={job.parse_warnings ?? []}
        />
      ) : (
        <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
          Für diesen älteren Job ist keine strukturierte Vorschau verfügbar.
        </p>
      )}

      {job.completion_pending && !job.interrupted && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          <p className="font-medium">Beleg gespeichert, wird noch fertiggestellt</p>
          <p className="mt-1">
            Beleg und PDF sind gespeichert. Sobald alle Dateien durch sind, wird noch einmal
            kurz aufgeräumt – danach erscheint der Beleg in den Listen. Du musst nichts tun.
          </p>
        </div>
      )}

      {job.interrupted && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-medium">{job.supplier === "HILTI" ? "Abschluss noch nicht bestätigt" : "Import abgebrochen"}</p>
          <p className="mt-1">
            {job.supplier === "HILTI" ? "Die Übernahme dauert länger als erwartet. Belegdaten können bereits gespeichert sein. Prüfe den Stand im Importverlauf, bevor du erneut hochlädst; ein laufender Import kann noch fertig werden." : <>Der Import hat begonnen, ist aber nicht fertig geworden. Die erkannten Belegdaten
            können schon gespeichert sein, das PDF fehlt dann möglicherweise. Es läuft nichts
            mehr weiter. Lade dieselbe Datei einfach noch einmal hoch – doppelt angelegt wird
            dabei nichts.</>}
          </p>
        </div>
      )}

      {job.status === "failed" && job.present_doc_id ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <p className="font-medium">Der Beleg ist trotzdem übernommen.</p>
          <p className="mt-1">
            Dieser Import ist erst nach dem Speichern der Belegdaten abgebrochen. Beleg und
            Positionen sind vorhanden; ob auch das Original-PDF da ist, siehst du im Beleg.
          </p>
          <Link
            className="mt-2 inline-block font-medium underline underline-offset-2"
            to={`/belege/${encodeURIComponent(job.present_doc_id)}`}
            state={{ from: `/imports?job=${encodeURIComponent(job.job_id)}` }}
          >
            Beleg ansehen →
          </Link>
        </div>
      ) : job.error_message && (
        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {safeUiMessage(job.error_message)}
        </p>
      )}

      {job.status === "completed" &&
        uploadPreview?.doc_id &&
        ["rechnung", "gutschrift"].includes(uploadPreview.doc_type ?? "") && (
          <p className={`rounded-md border p-3 text-sm ${alreadyPresent ? "border-slate-200 bg-slate-50 text-slate-700" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`}>
            <Link
              to={`/belege/${encodeURIComponent(uploadPreview.doc_id)}`}
              state={{ from: `/imports?job=${encodeURIComponent(job.job_id)}` }}
              className="font-medium text-emerald-800 underline hover:text-emerald-900"
            >{alreadyPresent ? "Vorhandenen Beleg öffnen" : "Beleg öffnen"}</Link>{" "}
            – Positionen, Beträge und Original ansehen.
          </p>
        )}

      {job.status === "completed" && !alreadyPresent &&
        uploadPreview?.doc_type === "angebot" &&
        uploadPreview.doc_id && (
          <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            Angebot übernommen.{" "}
            <Link
              to={`/belege/${encodeURIComponent(uploadPreview.doc_id)}`}
              className="font-medium text-emerald-800 underline hover:text-emerald-900"
            >
              Angebot jetzt prüfen
            </Link>{" "}
            — Zielpreise und Differenz zur Angebotssumme.
          </p>
        )}

      {job.status === "completed" && !alreadyPresent &&
        uploadPreview?.doc_type === "einzelauftrag" &&
        uploadPreview.doc_id && (
          <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            Auftragsbestätigung übernommen.{" "}
            <Link
              to={"/belege/" + encodeURIComponent(uploadPreview.doc_id)}
              className="font-medium text-emerald-800 underline hover:text-emerald-900"
            >
              Auftragsbestätigung jetzt prüfen
            </Link>{" "}
            — Preise vor der weiteren Verarbeitung intern freigeben oder
            rückfragen.
          </p>
        )}

      {job.status === "review" && (
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => props.onConfirm(job.job_id)}
            disabled={props.confirmPending || !canConfirm}
            className={PRIMARY_BUTTON_CLASS}
          >
            Übernehmen
          </button>
          <button
            type="button"
            onClick={() => props.onReject(job.job_id)}
            disabled={props.confirmPending}
            className={NEUTRAL_BUTTON_CLASS}
          >
            Ablehnen
          </button>
        </div>
      )}
      {!canConfirm && job.status === "review" && gcPreview && (
        <p className="text-sm text-amber-700">
          Die erforderlichen strukturierten Quellen fehlen noch.
        </p>
      )}
      {uploadBlockedByDetection && uploadPreview?.intake_kind !== "single_pdf" && job.status === "review" && (
        <p className="text-sm text-amber-700">
          Auto-Erkennung unsicher. Belegtyp und Großhändler manuell wählen und erneut prüfen.
        </p>
      )}
      {uploadBlockedByCapabilities && !uploadBlockedByDetection && job.status === "review" && (
        <p className="text-sm text-amber-700">
          {safeUiMessage(
            uploadPreview?.import_capabilities?.review_only_reason ||
              "Diese Quelle ist aktuell nur für Review/Staging freigegeben.",
          )}
        </p>
      )}
      {props.confirmError && (
        <p className="text-sm text-red-600">{safeUiMessage(props.confirmError)}</p>
      )}
    </div>
  );
}

function GcSupplierBatchPreviewSection({ preview }: { preview: GcSupplierBatchPreview }) {
  return (
    <div className="mt-5 space-y-4">
      <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <Metric label="UGL-/DATANORM-Dateien" value={preview.source_coverage.structured_file_count} />
        <Metric label="Rechnungs-PDFs" value={preview.source_coverage.invoice_pdf_count} />
        <Metric label="Lieferscheine" value={preview.source_coverage.delivery_note_pdf_count} />
        <Metric
          label="Import bereit"
          value={preview.source_coverage.required_structured_sources_ready ? "Ja" : "Nein"}
        />
      </dl>

      <div>
        <h3 className="text-sm font-medium text-slate-900">Quellenprofil</h3>
        <ul className="mt-2 space-y-1 text-sm text-slate-600">
          {preview.capabilities.required_structured_sources.map((source) => (
            <li key={source}>Erforderlich: {sourceFormatLabel(source)}</li>
          ))}
          {preview.capabilities.missing_structured_sources.map((source) => (
            <li key={`missing-${source}`} className="text-amber-700">
              Fehlt: {sourceFormatLabel(source)}
            </li>
          ))}
        </ul>
      </div>

      <MessageList title="Warnungen" messages={preview.warnings} tone="warning" />
      <MessageList title="Blocker" messages={preview.errors} tone="error" />
      {preview.capabilities.degraded_reasons.length > 0 && (
        <MessageList
          title="Eingeschränkte Funktionen"
          messages={preview.capabilities.degraded_reasons}
          tone="neutral"
        />
      )}
    </div>
  );
}

function UploadPreviewSection(props: {
  onPage?: (page: number) => void;
  preview: UploadImportPreview;
  status: string;
  warnings: Array<{ code: string; message: string }>;
}) {
  const acceptance = props.preview.acceptance_policy;
  if (props.preview.preview_type === "source_intake") {
    return (
      <SourceIntakePreviewSection
        preview={props.preview}
        status={props.status}
        warnings={props.warnings}
      />
    );
  }
  return (
    <div className="space-y-5">
      <section aria-label="Erkannte Belegdaten">
        <h3 className="mb-3 font-semibold text-slate-900">Erkannte Belegdaten</h3>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-5 gap-y-2 text-sm [&_dt]:text-slate-500 [&_dd]:break-words">
          <dt>Großhändler</dt><dd>{props.preview.supplier || "–"}</dd>
          <dt>Belegtyp</dt><dd>{props.preview.doc_type ? docTypeLabel(props.preview.doc_type) : "–"}</dd>
          <dt>Belegnummer</dt><dd>{safeDisplayName(props.preview.doc_number || props.preview.doc_id) || "–"}</dd>
          <dt>Belegdatum</dt><dd>{props.preview.doc_date || "–"}</dd>
          <dt>Umfang</dt><dd>{props.preview.line_count ?? "–"} Positionen · {props.preview.page_count ?? "–"} Seiten</dd>
          <dt>Währung</dt><dd>{props.preview.currency || "Nicht erkannt"}</dd>
        </dl>
      </section>
      <section aria-label="Erkannte Positionen">
        <h3 className="font-semibold text-slate-900">Erkannte Positionen</h3>
        <p className="mb-3 mt-1 text-xs text-slate-500">Mengen und Nettowerte aus dem PDF. Über den Seitenverweis kannst du die Angaben im Original vergleichen. Ein Strich bedeutet: nicht erkannt.</p>
        {props.preview.lines?.length ? <ul className="divide-y divide-slate-200 rounded-md border border-slate-200">
          {props.preview.lines.map((line, index) => <li key={`${line.line_pos}-${index}`} className="p-3 text-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><p className="break-words font-medium">{line.description || "Ohne Beschreibung"}</p><p className="mt-1 text-xs text-slate-500">Pos. {line.line_pos} · Artikel {line.article_code || "–"}</p></div>
              {line.page_no && <button type="button" onClick={() => props.onPage?.(line.page_no!)} className="shrink-0 text-emerald-700 underline underline-offset-2">Seite {line.page_no}</button>}
            </div>
            <dl className="mt-3 grid grid-cols-3 gap-3 text-xs">
              <div><dt className="text-slate-500">Menge</dt><dd className="mt-1 text-sm tabular-nums">{previewNumber(line.qty)} {line.uom}</dd></div>
              <div><dt className="text-slate-500">Einzelpreis netto</dt><dd className="mt-1 text-sm tabular-nums">{previewNumber(line.net_unit_price)}</dd></div>
              <div><dt className="text-slate-500">Betrag netto</dt><dd className="mt-1 text-sm tabular-nums">{previewNumber(line.line_net_amount)}</dd></div>
            </dl>
          </li>)}
        </ul> : <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">{props.preview.lines ? "Keine Positionen erkannt. Vergleiche den Beleg mit dem Original-PDF." : "Für diesen Import sind keine einzelnen Positionswerte gespeichert."}</p>}
      </section>
      {acceptance && !props.preview.document_completeness?.issues.length && <p className="text-sm text-slate-600">{acceptanceDecisionLabel(acceptance.decision, props.status)}</p>}
      <MessageList title="Bitte prüfen" messages={[...new Set([
        ...props.warnings.filter(warning => !warning.code.startsWith("document_")).map(warning => signalLabel(warning.code, warning.message)),
        ...(acceptance?.signals.filter(signal => signal.decision !== "pass" && !signal.code.startsWith("document_")).map(signal => signalLabel(signal.code, signal.message)) ?? []),
      ])]} tone={acceptance?.decision === "fail" ? "error" : "warning"} />
      {props.preview.detection && <details className="text-sm text-slate-600">
        <summary className="cursor-pointer">Details zur Erkennung</summary>
        <DetectionSection detection={props.preview.detection} manualSelection={props.preview.manual_selection} />
      </details>}

    </div>
  );
}

function previewNumber(value: string | null): string {
  if (value === null) return "–";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toLocaleString("de-AT", { maximumFractionDigits: 4 }) : "–";
}

function SourceIntakePreviewSection(props: {
  preview: UploadImportPreview;
  status: string;
  warnings: Array<{ code: string; message: string }>;
}) {
  const acceptance = props.preview.acceptance_policy;
  const sourceCounts = props.preview.source_counts;
  if (props.preview.intake_kind === "single_pdf") {
    return <section className="space-y-4">
      <h3 className="font-semibold text-slate-900">Belegart noch offen</h3>
      <p className="text-sm text-slate-600">Die Belegart konnte anhand des PDFs noch nicht eindeutig bestimmt werden. Deshalb wurden noch keine Positionswerte übernommen.</p>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-5 gap-y-2 text-sm">
        <dt className="text-slate-500">Großhändler</dt><dd>{props.preview.supplier || "Nicht erkannt"}</dd>
        <dt className="text-slate-500">Belegart</dt><dd>Noch nicht eindeutig erkannt</dd>
      </dl>
      <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">Wähle oben die passende Belegart und klicke auf „Erneut prüfen“.</p>
    </section>;
  }
  return (
    <div className="mt-5 space-y-4">
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-5 gap-y-2 text-sm [&_dt]:text-slate-500">
        <dt>Großhändler</dt><dd>{props.preview.supplier || "–"}</dd>
        <dt>Quelle</dt><dd>{intakeKindLabel(props.preview.intake_kind)}</dd>
        <dt>Dateien</dt><dd>{props.preview.file_count ?? "–"}</dd>
        <dt>PDF / UGL / DATANORM</dt><dd>{sourceCounts?.pdf ?? 0} / {sourceCounts?.ugl ?? 0} / {sourceCounts?.datanorm ?? 0}</dd>
      </dl>
      {props.preview.import_capabilities?.review_only_reason && (
        <MessageList
          title="Status"
          messages={[props.preview.import_capabilities.review_only_reason]}
          tone="neutral"
        />
      )}
      {props.preview.detection && (
        <DetectionSection
          detection={props.preview.detection}
          manualSelection={props.preview.manual_selection}
        />
      )}
      {acceptance && (
        <AcceptanceSection acceptance={acceptance} status={props.status} />
      )}
      <MessageList
        title="Import-Hinweise"
        messages={props.warnings.map((warning) => signalLabel(warning.code, warning.message))}
        tone="warning"
      />
    </div>
  );
}

function DetectionSection(props: {
  detection: NonNullable<UploadImportPreview["detection"]>;
  manualSelection: UploadImportPreview["manual_selection"];
}) {
  const supplierConfidence = confidenceLabel(props.detection.supplier_confidence);
  const docTypeConfidence = confidenceLabel(props.detection.doc_type_confidence);
  const mode = props.manualSelection?.fully_manual ? "Manuell" : "Automatisch";
  return (
    <div className="rounded-md border border-slate-100 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-slate-900">Erkennung</h3>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            props.detection.review_required
              ? "bg-amber-100 text-amber-700"
              : "bg-emerald-100 text-emerald-700"
          }`}
        >
          {props.detection.review_required ? "Prüfen" : "Sicher"}
        </span>
      </div>
      <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs text-slate-500">Großhändler</dt>
          <dd className="font-medium text-slate-900">
            {props.detection.supplier || "–"} · {supplierConfidence}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Belegtyp</dt>
          <dd className="font-medium text-slate-900">
            {props.detection.doc_type ? docTypeLabel(props.detection.doc_type) : "–"} · {docTypeConfidence}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Modus</dt>
          <dd className="font-medium text-slate-900">{mode}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Basis</dt>
          <dd className="font-medium text-slate-900">
            {confidenceLabel(props.detection.confidence_score)}
          </dd>
        </div>
      </dl>
      {props.detection.review_reason && (
        <p className="mt-3 text-sm text-amber-700">
          {safeUiMessage(props.detection.review_reason)}
        </p>
      )}
    </div>
  );
}

function AcceptanceSection({
  acceptance,
  status,
}: {
  acceptance: ImportAcceptancePolicy;
  status: string;
}) {
  return (
    <div>
      <h3 className="text-sm font-medium text-slate-900">Importentscheidung</h3>
      <p className="mt-1 text-sm text-slate-600">
        {acceptanceDecisionLabel(acceptance.decision, status)}
      </p>
      {acceptance.signals.length > 0 && (
        <ul className="mt-2 space-y-1 text-sm text-amber-800">
          {acceptance.signals.map((signal) => (
            <li key={`${signal.source}-${signal.code}`}>{signalLabel(signal.code, signal.message)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Metric(props: { label: string; value: string | number }) {
  return (
    <div className="min-w-0 rounded-md border border-slate-100 p-3">
      <dt className="text-xs text-slate-500">{props.label}</dt>
      <dd className="mt-1 break-words font-medium text-slate-900">{props.value}</dd>
    </div>
  );
}

function MessageList(props: {
  title: string;
  messages: string[];
  tone: "warning" | "error" | "neutral";
}) {
  if (props.messages.length === 0) return null;
  const color =
    props.tone === "error"
      ? "text-red-700 bg-red-50"
      : props.tone === "warning"
        ? "text-amber-800 bg-amber-50"
        : "text-slate-600 bg-slate-50";
  return (
    <div className={`min-w-0 rounded-md p-3 ${color}`}>
      <h3 className="text-sm font-medium">{props.title}</h3>
      <ul className="mt-2 space-y-1 text-sm">
        {props.messages.map((message, index) => (
          <li key={`${message}-${index}`} className="break-words">{safeUiMessage(message)}</li>
        ))}
      </ul>
    </div>
  );
}

function gcSupplierBatchPreview(value: unknown): GcSupplierBatchPreview | null {
  if (!isRecord(value)) return null;
  return value.preview_type === "gc_supplier_batch"
    ? (value as GcSupplierBatchPreview)
    : null;
}

function uploadImportPreview(value: unknown): UploadImportPreview | null {
  if (!isRecord(value)) return null;
  if (value.preview_type === "gc_supplier_batch") return null;
  return value as UploadImportPreview;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function sourceTypeLabel(sourceType: string) {
  return SOURCE_TYPE_LABELS[sourceType] ?? "Import";
}

function selectedUploadValue(value: string | null) {
  const selected = value?.trim() ?? "";
  if (!selected || selected === AUTO_SELECT_VALUE) return null;
  return selected;
}

function sourceFormatLabel(source: string) {
  if (source === "ugl") return "UGL";
  if (source === "datanorm") return "DATANORM";
  if (source === "pdf") return "PDF";
  return source.toUpperCase();
}

function docTypeLabel(docType: string) {
  return importDocTypeLabel(docType);
}

function intakeKindLabel(value: string | null | undefined) {
  const labels: Record<string, string> = {
    single_pdf: "Einzel-PDF",
    pdf_batch: "PDF-Batch",
    structured_ugl: "UGL",
    structured_datanorm: "DATANORM",
    structured_batch: "Strukturierter Batch",
    mixed_batch: "Gemischter Batch",
    unsupported_archive: "Nicht unterstütztes Archiv",
    unsupported: "Nicht unterstützt",
  };
  return value ? labels[value] ?? value : "–";
}

function acceptanceDecisionLabel(decision: string, status?: string) {
  if (status === "duplicate") return "Bereits vorhanden – kein neuer Beleg angelegt";
  if (status === "interrupted") return "Import abgebrochen";
  if (status === "completion_pending") return "Beleg gespeichert, wird noch fertiggestellt";
  if (status === "completed") return "Übernommen";
  if (status === "confirmed" || status === "importing") return "Wird gespeichert";
  if (decision === "pass" && status === "review") {
    return "Bereit zur Übernahme - Bestätigung erforderlich";
  }
  if (decision === "pass") return "Bereit zur Übernahme";
  if (decision === "fail") return "Blockiert";
  if (decision === "review_required") return "Manuelle Prüfung erforderlich";
  return "Hinweis aus der Importprüfung";
}

function confidenceLabel(value: number | null | undefined) {
  if (typeof value !== "number" || Number.isNaN(value)) return "–";
  return `${Math.round(value * 100)}%`;
}

function signalLabel(code: string, fallback: string) {
  const labels: Record<string, string> = {
    no_doc_id: "Belegnummer wurde nicht erkannt.",
    no_lines: "Keine Positionen erkannt.",
    parser_unavailable: "Parser ist nicht verfügbar.",
    parse_error: "Parser konnte die Datei nicht sicher lesen.",
    parse_validation_unavailable:
      "Die Qualitätsprüfung ist vorübergehend nicht verfügbar. Bitte später erneut versuchen.",
    reconciliation_unavailable:
      "Der Belegabgleich ist vorübergehend nicht verfügbar. Bitte später erneut versuchen.",
    source_coverage_unavailable:
      "Die Quellenprüfung ist vorübergehend nicht verfügbar. Bitte später erneut versuchen.",
    set_price_qa_unavailable:
      "Die Setpreis-Prüfung ist vorübergehend nicht verfügbar. Bitte später erneut versuchen.",
    low_confidence: "Die Erkennung benötigt eine manuelle Prüfung.",
    auto_detection_review_required: fallback,
    source_supplier_review_required: fallback,
    source_intake_review_only: fallback,
    source_intake_review_required: fallback,
    unsupported_source_entries: fallback,
    unsupported_archive_entries: fallback,
    nested_archive_classified_by_name: fallback,
    gc_batch_warning: fallback,
    gc_batch_validation_error: fallback,
  };
  return labels[code] ?? safeUiMessage(fallback || "Hinweis aus der Importprüfung.");
}

function safeUiMessage(value: unknown) {
  if (typeof value !== "string") return "Unerwarteter Fehler.";
  return value
    .replace(/(^|[^\w:])\/(?:[^\s,;:'")]+\/?)+/g, "$1<lokaler Pfad>")
    .replace(/\b(token|cookie|password|secret|api[_-]?key)\s*=\s*[^\s,;]+/gi, "<redacted>");
}
